/* NZS cart transport guard: serialise unknown cart writes and surface failures. */
(function () {
  'use strict';
  if (window.__nzsCartGuardInstalled) return;
  window.__nzsCartGuardInstalled = true;

  var innerFetch = window.fetch;
  var tail = Promise.resolve();
  var engineTail = Promise.resolve();
  var LOCK_TIMEOUT = 10000;
  var POLLCACHE_ENABLED = true;
  var POLLCACHE_TTL = 20000;
  var pollCache = { text: '', ts: 0 };
  var pollWriteEpoch = 0;
  var pollWritesInFlight = 0;
  var BREAKER_ENABLED = true;
  var BREAKER_COOLDOWN = 60000;
  var BREAKER_MAX = 32;
  var breakerState = {};
  var breakerOrder = [];
  var routeNames = [
    'add', 'add.js',
    'change', 'change.js',
    'update', 'update.js',
    'clear', 'clear.js'
  ];

  function routeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function routePaths() {
    return routeNames.map(function (name) {
      return new URL(routeRoot() + 'cart/' + name, location.href).pathname;
    });
  }

  function requestInfo(input, init) {
    try {
      var request = typeof Request !== 'undefined' && input instanceof Request;
      var url = new URL(request ? input.url : input, location.href);
      var method = String(
        init && init.method != null ? init.method : (request ? input.method : 'GET')
      ).toUpperCase();
      var keepalive = init && init.keepalive != null
        ? init.keepalive
        : !!(request && input.keepalive);
      return {
        url: url,
        method: method,
        keepalive: keepalive,
        cartWrite: url.origin === location.origin &&
          method === 'POST' &&
          routePaths().indexOf(url.pathname) !== -1
      };
    } catch (e) {
      return { cartWrite: false, keepalive: false };
    }
  }

  function describe(data) {
    if (!data) return '';
    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.errors === 'string') return data.errors;
    if (data.errors && typeof data.errors === 'object') {
      return Object.keys(data.errors).map(function (key) {
        return [].concat(data.errors[key]).join(', ');
      }).join(', ');
    }
    return '';
  }

  function emit(res, info) {
    var clone;
    try { clone = res.clone(); } catch (e) { clone = null; }
    var body = clone ? clone.text().then(function (raw) {
      try { return describe(JSON.parse(raw)); } catch (e) { return ''; }
    }, function () { return ''; }) : Promise.resolve('');

    body.then(function (message) {
      if (typeof document === 'undefined' || typeof CustomEvent !== 'function') return;
      document.dispatchEvent(new CustomEvent('nzs:cart-error', {
        detail: {
          message: message || (res.status === 429
            ? 'Too many cart updates — please wait a moment and try again.'
            : "Couldn't update cart"),
          status: res.status,
          url: info.url && info.url.pathname
        }
      }));
    });
  }

  function inspect(promise, info) {
    return promise.then(function (res) {
      /* NZS-GUARD-QUIET (Blake 2026-08-01): 429 and 5xx are transient -- every first-party
         caller retries them, so at this layer they are backpressure, not a verdict. Alarming
         here painted "CART UPDATE FAILED" over updates that went on to succeed a moment later.
         Definitive rejections (a 4xx that is not a rate limit) still toast. */
      /* NZS-GUARD-APP-QUIET (Blake 2026-08-01): app-attributed writes own their UI feedback. */
      var appWrite = !!(info.url && info.url.searchParams && info.url.searchParams.has('app'));
      if (!appWrite && !res.ok && res.status !== 429 && res.status < 500) emit(res, info);
      return res;
    });
  }

  /* NZS-GUARD-POLL-CACHE (Blake 2026-08-01): coalesce app cart reads between real writes. */
  function pollRequest(input, info) {
    try {
      if (!POLLCACHE_ENABLED || !info.url || info.method !== 'GET' ||
          typeof Response !== 'function' ||
          (typeof input !== 'string' &&
           !(typeof URL !== 'undefined' && input instanceof URL)) ||
          !info.url.searchParams.has('app')) return null;
      var cartPath = new URL(routeRoot() + 'cart.js', location.href).pathname;
      if (info.url.pathname !== cartPath) return null;
      if (pollCache.text && Date.now() - pollCache.ts < POLLCACHE_TTL) {
        return {
          response: new Response(pollCache.text, {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          })
        };
      }
      return {};
    } catch (e) {
      return null;
    }
  }

  function pollFetch(call) {
    var epoch = pollWriteEpoch;
    var writeInFlight = pollWritesInFlight > 0;
    var promise;
    try { promise = Promise.resolve(call()); }
    catch (cause) { promise = Promise.reject(cause); }
    return promise.then(function (res) {
      try {
        if (!res.ok) return res;
        var clone = res.clone();
        clone.text().then(function (raw) {
          try {
            var data = JSON.parse(raw);
            if (!data || typeof data !== 'object' || Array.isArray(data) ||
                !Array.isArray(data.items) || epoch !== pollWriteEpoch ||
                writeInFlight || pollWritesInFlight > 0) return;
            pollCache.text = raw;
            pollCache.ts = Date.now();
          } catch (e) {}
        }, function () {});
      } catch (e) {}
      return res;
    });
  }

  function pollWriteFetch(call) {
    pollWriteEpoch += 1;
    pollWritesInFlight += 1;
    pollCache.text = '';
    pollCache.ts = 0;
    var promise;
    try { promise = Promise.resolve(call()); }
    catch (cause) { promise = Promise.reject(cause); }
    return promise.then(function (res) {
      pollWritesInFlight -= 1;
      return res;
    }, function (cause) {
      pollWritesInFlight -= 1;
      throw cause;
    });
  }

  /* NZS-GUARD-KITE-BREAKER (Blake 2026-08-01): cap repeated marker-bearing app adds. */
  function breakerEntry(signature) {
    var entry = breakerState[signature];
    if (entry) return entry;
    if (breakerOrder.length >= BREAKER_MAX) {
      delete breakerState[breakerOrder.shift()];
    }
    entry = { failures: 0, body: '', status: 422, until: 0, probing: false };
    breakerState[signature] = entry;
    breakerOrder.push(signature);
    return entry;
  }

  function clearBreaker(signature, entry) {
    if (breakerState[signature] !== entry) return;
    delete breakerState[signature];
    var index = breakerOrder.indexOf(signature);
    if (index !== -1) breakerOrder.splice(index, 1);
  }

  function cacheBreakerBody(res, entry, arm) {
    entry.status = res.status || 422;
    var clone;
    try { clone = res.clone(); } catch (e) { return; }
    clone.text().then(function (raw) {
      try {
        if (!raw) return;
        entry.body = raw;
        if (arm && entry.failures >= 2) entry.until = Date.now() + BREAKER_COOLDOWN;
      } catch (e) {}
    }, function () {});
  }

  function breakerRequest(input, init, info) {
    try {
      if (!BREAKER_ENABLED || !info.cartWrite || !info.url ||
          typeof Response !== 'function' ||
          !info.url.searchParams.has('app')) return null;
      var addPath = new URL(routeRoot() + 'cart/add', location.href).pathname;
      if (info.url.pathname !== addPath && info.url.pathname !== addPath + '.js') return null;
      if (typeof input !== 'string' &&
          !(typeof URL !== 'undefined' && input instanceof URL)) return null;
      if (!init || typeof init.body !== 'string') return null;

      var data = JSON.parse(init.body);
      var items = Array.isArray(data.items) ? data.items : [data];
      var ids = [];
      var marked = false;
      items.forEach(function (item) {
        if (!item || typeof item !== 'object') return;
        var properties = item.properties;
        if (properties && typeof properties === 'object' &&
            (Object.prototype.hasOwnProperty.call(properties, '_free_product') ||
             Object.prototype.hasOwnProperty.call(properties, '_rule_id'))) marked = true;
        if (item.id != null && String(item.id)) ids.push(String(item.id));
      });
      if (!marked || !ids.length) return null;

      var signature = info.url.pathname + ':' + ids.sort().join(',');
      var entry = breakerEntry(signature);
      if (entry.until && entry.body && entry.failures >= 2) {
        if (Date.now() < entry.until || entry.probing) {
          return {
            entry: entry,
            signature: signature,
            response: new Response(entry.body, {
              status: entry.status,
              headers: { 'Content-Type': 'application/json' }
            })
          };
        }
        entry.probing = true;
        return { entry: entry, signature: signature, probe: true };
      }
      return { entry: entry, signature: signature, probe: false };
    } catch (e) {
      return null;
    }
  }

  function breakerFetch(call, state) {
    var promise;
    state.fetchStarted = true;
    try { promise = Promise.resolve(call()); }
    catch (cause) { promise = Promise.reject(cause); }
    return promise.then(function (res) {
      try {
        /* NZS-ROUND4C: count CF-edge 429s from no-backoff app loops like 422s. */
        if (state.probe) {
          state.entry.probing = false;
          if (res.status >= 200 && res.status < 300) {
            clearBreaker(state.signature, state.entry);
          } else {
            state.entry.until = Date.now() + BREAKER_COOLDOWN;
            if (res.status === 422 || res.status === 429) cacheBreakerBody(res, state.entry, false);
          }
        } else if (res.status === 422 || res.status === 429) {
          state.entry.failures += 1;
          if (state.entry.failures >= 2 && state.entry.body) {
            state.entry.until = Date.now() + BREAKER_COOLDOWN;
          }
          cacheBreakerBody(res, state.entry, true);
        }
      } catch (e) {}
      return res;
    }, function (cause) {
      try {
        if (state.probe) {
          state.entry.probing = false;
          state.entry.until = Date.now() + BREAKER_COOLDOWN;
        }
      } catch (e) {}
      throw cause;
    });
  }

  function breakerDispatch(promise, state) {
    if (!state || !state.probe) return promise;
    return promise.then(function (result) {
      try { if (!state.fetchStarted) state.entry.probing = false; } catch (e) {}
      return result;
    }, function (cause) {
      try { if (!state.fetchStarted) state.entry.probing = false; } catch (e) {}
      throw cause;
    });
  }

  function run(task) {
    var fence = { released: false };
    var previous = tail;
    var release;
    tail = new Promise(function (resolve) { release = resolve; });

    var begin = function () {
      var result;
      try { result = Promise.resolve(task(fence)); }
      catch (cause) { result = Promise.reject(cause); }
      var timer;
      var ceiling = new Promise(function (resolve) {
        timer = setTimeout(function () {
          fence.released = true;
          resolve();
        }, LOCK_TIMEOUT);
      });
      var finish = function () {
        fence.released = true;
        clearTimeout(timer);
        release();
      };
      Promise.race([result, ceiling]).then(finish, finish);
      return result;
    };
    return previous.then(begin, begin);
  }

  window.fetch = function (input, init) {
    var args = arguments;
    var info = requestInfo(input, init);
    var handled = !!(init && init.__nzsCartGuardHandled);
    var bypass = !!(init && init.__nzsCartGuardBypass);
    var fence = init && init.__nzsCartGuardFence;
    var call = function () { return innerFetch.apply(window, args); };
    var breaker = breakerRequest(input, init, info);
    var poll = pollRequest(input, info);

    /* Synthetic failures have no transport work and bypass every serialization queue. */
    if (breaker && breaker.response) return Promise.resolve(breaker.response);
    if (poll && poll.response) return Promise.resolve(poll.response);
    if (POLLCACHE_ENABLED && info.cartWrite) {
      var writeCall = call;
      call = function () { return pollWriteFetch(writeCall); };
    }
    if (poll) {
      var pollCall = call;
      call = function () { return pollFetch(pollCall); };
    }
    if (breaker) {
      var networkCall = call;
      call = function () { return breakerFetch(networkCall, breaker); };
    }

    /* The explicit engine already holds this queue across snapshot and reconcile. */
    if (bypass) {
      var guardedCall = function () {
        if (fence && fence.released) return Promise.reject(new Error('Cart guard timed out'));
        return call();
      };
      var result = breakerDispatch(engineTail.then(guardedCall, guardedCall), breaker);
      engineTail = result.then(function () {}, function () {});
      return result;
    }
    if (!info.cartWrite) return breakerDispatch(call(), breaker);
    if (info.keepalive) {
      return breakerDispatch(handled ? call() : inspect(call(), info), breaker);
    }
    return breakerDispatch(run(function () {
      var result = call();
      return handled ? result : inspect(result, info);
    }), breaker);
  };

  window.nzsCartGuard = {
    run: run,
    _isCartWrite: function (input, init) { return requestInfo(input, init).cartWrite; }
  };
})();
